import cn from 'classnames'
import React, { Fragment, memo } from 'react'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import { CheckDataList } from 'src/components/global/ListingPage/CheckDataList'
import { DottedDataList } from 'src/components/global/ListingPage/DottedDataList'
import { IconLink } from 'src/components/global/ListingPage/IconLink'
import { SpanDataIcon } from 'src/components/global/ListingPage/SpanDataIcon'
import { SpanDataList } from 'src/components/global/ListingPage/SpanDataList'
import { LongDescItem } from 'src/components/global/Overview/LongDescItem'
import { ShortDescItem } from 'src/components/global/Overview/ShortDescItem'
import { DedicatedResourcesType, SectionItem } from 'src/types'
import { checkNotEmpty } from 'src/utils/checkNotEmpty'

import CartHeader from '../Cart/CartHeader'
import CartHeaderTitle from '../Cart/CartHeaderTitle'
import CartSubContent from '../Cart/CartSubContent'
import CartSubTitle from '../Cart/CartSubTitle'
import { IconLinkLinkedin } from '../ListingPage/IconLinkLinkedin'
import { IconLinkTwitter } from '../ListingPage/IconLinkTwitter'

interface SectionProps {
  title: string
  shortDescription?: string
  description: string
  url: string
  items: SectionItem[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderItem?: (item: any) => React.ReactNode
  content?: string | TrustedHTML
}

export const Section: React.FC<SectionProps> = memo(
  ({ title, shortDescription, description, url, items, renderItem, content }) => {
    const randomNum = Math.floor(Math.random() * 1000) // Generates a number between 0 and 999

    return (
      <div key={randomNum + 'SectionComp'} className="w-full border-[1px] border-mist rounded-[5px] mt-[2rem]">
        <CartHeader>
          <CartHeaderTitle>{title}</CartHeaderTitle>
        </CartHeader>

        <div className="flex flex-col mb-[2.9375rem] mt-[1.9375rem]">
          {shortDescription && <ShortDescItem shortDescription={shortDescription} />}

          {checkNotEmpty(description) && (
            <div className="value-proposition">
              <LongDescItem longDescription={description} />{' '}
            </div>
          )}

          <div className="flex flex-wrap w-full px-[2.0rem] relative">
            {checkNotEmpty(url) && (
              <div className={'flex flex-col w-1/2'}>
                <div className="flex flex-col mb-[2.0rem]">
                  <CartSubTitle>RESOURCES</CartSubTitle>
                  <IconLink url={url} title="Official website" iconColor="#011D58" />
                </div>
              </div>
            )}
            {items.map((item: SectionItem, index: number) => (
              <Fragment key={index}>
                {item.type === 'LinkedinIconLink' && (
                  <div className="absolute top-0 right-7 flex flex-col mb-[2.0rem]">
                    <IconLinkLinkedin url={item.items[0]} />
                  </div>
                )}
                {item.type === 'TwitterIconLink' && (
                  <div className="absolute top-10 right-7 flex flex-col mb-[2.0rem]">
                    <IconLinkTwitter url={item.items[0]} />
                  </div>
                )}
                {!['LinkedinIconLink', 'TwitterIconLink'].includes(item.type) && (
                  <div
                    className={cn(
                      'flex w-full',
                      item.isFullWidth ? '' : 'md:w-1/2',
                      item.isHorizontal ? 'flex-row' : 'flex-col',
                      item.className ? item.className : '',
                    )}
                  >
                    {item.type === 'SpanDataList' && item.subType && (
                      <div className="flex flex-col mb-[2.0rem]">
                        <CartSubTitle>{item.title}</CartSubTitle>
                        <SpanDataList
                          isProductTypes={item.title === 'PRODUCT TYPES'}
                          isPracticeAreas={item.title === 'PRACTICE AREAS'}
                          features={item.items}
                          type={item.subType}
                        />
                      </div>
                    )}
                    {item.type === 'SpanDataIcon' && (
                      <div className="flex flex-col mb-[2.0rem]">
                        <CartSubTitle>{item.title}</CartSubTitle>
                        <SpanDataIcon features={item.items} />
                      </div>
                    )}
                    {item.type === 'DottedDataList' && item.items.length > 0 && (
                      <div className="flex flex-col mb-[2.0rem]">
                        <div className="flex flex-col">
                          <CartSubTitle>{item.title}</CartSubTitle>
                          <DottedDataList
                            key={'DottedDataList-' + index}
                            isSecurity={
                              item.title === 'Security' ||
                              item.title === 'Total Funding Amount' ||
                              item.title === 'Latest Funding Round' ||
                              item.title === 'Value Proposition'
                            }
                            isDataHostingLocations={item.title === 'Data Hosting Locations'}
                            isServiceOfferings={item.title === 'SERVICE OFFERINGS'}
                            isValueProposition={item.title === 'Value Proposition'}
                            features={item.items || []}
                            colValue={item.colValue}
                            title={item.title}
                          />
                        </div>
                      </div>
                    )}
                    {item.type === 'string' && (
                      <div className="flex flex-col mb-[2.0rem]">
                        <div className="flex flex-col">
                          <CartSubTitle>{item.title}</CartSubTitle>
                          <div className="flex flex-wrap items-center">
                            <CartSubContent type="list" className="mr-[0.125rem] whitespace-pre-wrap list-none">
                              {item.title === 'Value Proposition' ? (
                                <div className="value-proposition">
                                  <div dangerouslySetInnerHTML={{ __html: item.sValue || '' }} />
                                </div>
                              ) : (
                                <div dangerouslySetInnerHTML={{ __html: item.sValue || '' }} />
                              )}
                            </CartSubContent>
                          </div>
                        </div>
                      </div>
                    )}
                    {item.type === 'number' && (
                      <div className="flex flex-col mb-[2.0rem]">
                        <div className="flex flex-col">
                          <CartSubTitle>{item.title}</CartSubTitle>
                          <div className="flex flex-wrap items-center">
                            <CartSubContent type="list" className="mr-[0.125rem] list-none">
                              {item.nValue || ''}
                            </CartSubContent>
                          </div>
                        </div>
                      </div>
                    )}
                    {item.type === 'dedicated' && (
                      <div className="flex flex-col mb-[2.0rem]">
                        <CartSubTitle>{item.title}</CartSubTitle>
                        <div>
                          {item.dValue &&
                            item.dValue.length > 0 &&
                            item.dValue.map((rc: DedicatedResourcesType, index: number) => {
                              const formattedType = rc.type.replace(/([a-z])([A-Z])/g, '$1 $2')
                              return (
                                <CartSubContent type="list" key={index} className="mr-[0.125rem]">
                                  {`${formattedType} : ${rc.count}`}
                                  {/* {index !== (item.dValue ? item.dValue.length : 0) - 1 ? ',' : ''} */}
                                </CartSubContent>
                              )
                            })}
                        </div>
                      </div>
                    )}
                    {item.type === 'CustomList' &&
                      renderItem &&
                      item.items.some(
                        (subItem) =>
                          (subItem.title && subItem.title.trim() !== '') ||
                          (subItem.externalLink && subItem.externalLink.trim() !== '') ||
                          (subItem.name && subItem.name.trim() !== ''),
                      ) && (
                        <div className="flex flex-col mb-[2.0rem]">
                          <CartSubTitle>{item.title}</CartSubTitle>
                          <ul className="custom-bullet flex flex-col pl-5 space-y-2 list-none">
                            {item.items
                              .filter((subItem) =>
                                [subItem?.title, subItem?.name].some((field) => field && field.trim() !== ''),
                              )
                              .map((subItem, idx) => (
                                <li key={idx} className="pl-4">
                                  {renderItem(subItem)}
                                </li>
                              ))}
                          </ul>
                        </div>
                      )}
                    {item.type === 'CheckDataList' && (
                      <div className="flex flex-col mb-[2.0rem]">
                        <div className="flex flex-col">
                          <CartSubTitle className="mb-[14px]">{item.title}</CartSubTitle>
                          <CheckDataList features={item.items || []} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Fragment>
            ))}
            {content && <HtmlContentViewer html={content} />}
          </div>
        </div>
      </div>
    )
  },
)
